import React, { useState, useEffect, useRef } from "react";
import Typography from "../components/Typography";
import { spacing } from "../utils/tokens";
import { useTheme } from "../context/ThemeContext";
import { useParams } from "react-router-dom";

import Header from "../components/Header";
import CompanyName from "../components/CompanyName";
import TimeSlider from "../components/TimeSlider";
import CompanySummary from "../components/CompanySummary";
import TickerNewsList from "../components/TickerNewsList";
import TickerPrice from "../components/TickerPrice";
import { fetchTickerNews, fetchTickerDetails, fetchStockInfo } from "../models/marketsService";

import Spacer from "../assets/svgs/Spacer.svg";
import KeyStats from "../assets/svgs/KeyStats.svg";
import KeyStats_Neg from "../assets/svgs/KeyStats_Neg.svg";
import Events from "../assets/svgs/Events.svg";
import Vibes from "../assets/svgs/Vibes.svg";
import CompanyProfile from "../assets/svgs/CompanyProfile.svg";
import { useNavigate } from "react-router-dom";

import CompanyChart from "./CompanyChart";
import { fetchStockHistory } from "../models/marketsService"; // ✅ Import stock history API
import { franc } from "franc-min";

// import "./Company.css";


// Dummy Data for Testing
import assets from "../models/assets.json";

import { motion } from "framer-motion"; // ✅ Import Framer Motion

const pageVariants = {
    hidden: { x: "100%", opacity: 0 },  // ✅ Enter from the right
    visible: { x: "0%", opacity: 1, transition: { duration: 0.4, ease: "easeOut" } }, // ✅ Slide in
    exit: { x: "100%", opacity: 0, transition: { duration: 0.3, ease: "easeIn" } }, // ✅ Slide out (when going back)
};

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.5, transition: { duration: 0.3 } }, // ✅ Dim background
    exit: { opacity: 0, transition: { duration: 0.3 } }, // ✅ Fade out background
};

const TABS = ["Essentials", "Key Stats", "Events", "Vibes", "Company Profile"];

function Company() {
    const { ticker } = useParams(); // Get the ticker from the URL
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState("Essentials");
    const [timeframe, setTimeframe] = useState("1D");
    const [allTickerNews, setAllTickerNews] = useState([]); // Store all news articles
    const [filteredNews, setFilteredNews] = useState([]); // Store filtered news
    const [tickerDetails, setTickerDetails] = useState({}); // Store ticker details
    const [tickerPrices, setTickerPrices] = useState({}); // Store ticker prices
    const [isLoading, setIsLoading] = useState(true); // ✅ New loading state
    const navigate = useNavigate();

    // Refs for Tab Navigation
    const containerRef = useRef(null);
    const tabNavRef = useRef(null);
    const tabContentRef = useRef(null);
    const topOffset = window.innerWidth < 769 ? 120 : 167; // Top offset for content section because of the sticky header
    const topDistance = window.innerWidth < 769 ? 60 : 113; // Top distance for the sticky header

    const isPositive = tickerPrices.todaysChange >= 0;

    const [stockHistory, setStockHistory] = useState([]);
    const [rawHistory, setRawHistory] = useState([]);

    const [hoveredPriceData, setHoveredPriceData] = useState({
        price: tickerPrices?.day?.c || 0, // Default to latest price
        priceChange: tickerPrices?.todaysChange || 0,
        percentageChange: tickerPrices?.todaysChangePerc || 0,
        timestamp: "", // Default to empty timestamp
    });

    // ✅ Function to update price data from CompanyChart
    const handlePriceUpdate = (updatedData) => {
        setHoveredPriceData(updatedData);
    };

    const sectionRefs = useRef(
        TABS.reduce((acc, tab) => {
          acc[tab] = React.createRef();
          return acc;
        }, {})
    ).current;

    // Fetch Stock Price History
    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const { normalizedHistory, trimmedPrices, data } = await fetchStockHistory(ticker);
                setRawHistory(data);
                setStockHistory(normalizedHistory);
            } catch (error) {
                console.error("Error fetching stock history:", error);
            }
        };
        fetchHistory();
    }, []);

    // Fetch Ticker News Once (on Component Mount)
    useEffect(() => {
        const fetchData = async () => {
    
            try {
                const [newsResponse, detailsResponse, pricesResponse] = await Promise.all([
                    fetchTickerNews(ticker, 800),
                    fetchTickerDetails(ticker),
                    fetchStockInfo(ticker)
                ]);

                // ✅ Filter news where title is detected as English (ISO-639-3 code: "eng") 
                const englishNews = (newsResponse.results || []).filter(article => {
                    return franc(article.title || "", { minLength: 3 }) === "eng";
                }).filter(article => article.title.length <= 60); // ✅ Filter titles ≤ 60 characters
    
                setAllTickerNews(englishNews || []);
                setTickerDetails(detailsResponse.results || {});
                setTickerPrices(pricesResponse.ticker || {});

                // ✅ Extract, filter out null values, and get unique publisher names
                // const uniquePublishers = [...new Set(
                // (newsResponse.results || [])
                //     .map(article => article.publisher?.name)
                //     .filter(Boolean) // Remove undefined/null values
                // )];

                // console.log("Unique Publishers:", uniquePublishers);
    
                console.log("✅ All data fetched successfully");
                } catch (error) {
                console.error("❌ Error fetching data:", error);
                } finally {
                setIsLoading(false); // ✅ Set loading to false when everything is done
                }
        };
    
        fetchData();
    }, []); // ✅ useEffect is always called, but fetchData checks ticker inside.

    // Handle Timeframe Filtering (Frontend)
    useEffect(() => {
    if (!allTickerNews.length) return;

    const now = new Date();
    const timeframeOffsets = {
        "1D": 1,
        "5D": 5,
        "1M": 30,
        "6M": 180,
        "1Y": 365,
        "3Y": 3 * 365,
        "5Y": 5 * 365
    };

    const timeframes = Object.keys(timeframeOffsets);
    let selectedTimeframe = timeframe;

    const getFilteredNews = (timeframeKey) => {
        const dateOffset = timeframeOffsets[timeframeKey];
        const pastDate = new Date(new Date().setDate(now.getDate() - dateOffset));

        return allTickerNews.filter(article => {
            const articleDate = new Date(article.published_utc);
            return articleDate >= pastDate;
        });
    };

    let filtered = getFilteredNews(selectedTimeframe);

    // If no news, find the next available timeframe with news
    if (!filtered.length) {
        for (let tf of timeframes) {
            if (tf !== selectedTimeframe) {
                filtered = getFilteredNews(tf);
                if (filtered.length) {
                    selectedTimeframe = tf;
                    break;
                }
            }
        }
    }

    // Shuffle once
    const shuffledNews = [...filtered].sort(() => Math.random() - 0.5);

    // Pick a random article count between 3 and 25
    const randomCount = Math.floor(Math.random() * (25 - 3 + 1)) + 3;
    const downsampledNews = shuffledNews.slice(0, randomCount);

    setFilteredNews(downsampledNews);

    }, [timeframe, allTickerNews]);

    // Handle Tab Navigation
    const disableScrollDetection = useRef(false); // Prevents conflict with manual tab clicks

    useEffect(() => {
        const container = tabContentRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (disableScrollDetection.current) return; // Skip scroll detection if manually switching tabs

            let currentTab = activeTab;
            let smallestOffset = Number.POSITIVE_INFINITY;

            Object.entries(sectionRefs).forEach(([tab, ref]) => {
                const section = ref.current;
                if (section) {
                    const sectionTop = section.getBoundingClientRect().top - topOffset;
                    const distance = Math.abs(sectionTop);

                    if (distance < smallestOffset) {
                        smallestOffset = distance;
                        currentTab = tab;
                    }
                }
            });

            if (currentTab !== activeTab) {
                setActiveTab(currentTab);
            }
        };

        // Attach listener
        container.addEventListener("scroll", handleScroll, { passive: true });

        // Force an initial check after loading completes
        requestAnimationFrame(() => handleScroll());

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [isLoading, activeTab, sectionRefs]); 
    

    // Scroll to the selected tab
    const handleTabClick = (tab) => {
    disableScrollDetection.current = true; // Disable auto-scroll detection temporarily
    setActiveTab(tab);

    const section = sectionRefs[tab].current;
    const container = tabContentRef.current;

    if (section && container) {
        container.scrollTo({
            top: section.offsetTop - topOffset,
            behavior: "smooth",
        });

        // ✅ Re-enable scroll detection after animation finishes
        setTimeout(() => {
            disableScrollDetection.current = false;
        }, 600); // Adjust timeout to match scroll speed
    }
    };

    const styles = {
        navBar: {
            position: "sticky",
            display: "flex", 
            top: topDistance,
            gap: spacing.m, 
            justifyContent: "flex-start", 
            width: "100%", 
            padding: `0 ${spacing.m}px`, 
            overflowX: "auto",  // ✅ Ensure horizontal scrolling
            overflowY: "hidden", // ✅ Prevent unnecessary vertical scroll
            whiteSpace: "nowrap", 
            backgroundColor: theme.colors.fillPrimary, 
            zIndex: 10, 
            borderBottom: `1px solid ${theme.colors.fillSecondary}`, 
            transition: "all 0.2s ease-in-out",
        },
        loadingContainer:{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
            backgroundColor: "#222222",
        }
    }


    // ✅ Don't render UI until data is loaded
    if (isLoading) {
        return <div style={styles.loadingContainer}>
            {/* <LetterGlitch /> */}
            <Typography variant="summary" type="utility" style={{color: theme.colors.contentInverse}}>Loading...</Typography>
        </div>;
    }
        


    return (

        <>
        {/* ✅ Background Dim Effect */}
        <motion.div 
                className="backdrop"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={backdropVariants}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "black",
                    zIndex: -8, // ✅ Make sure it’s behind Company page
                }}
            />
        <motion.div 
            initial="hidden" 
            animate="visible" 
            exit="exit" 
            variants={pageVariants} 
            ref={containerRef} 
            style={{ 
                display: "flex", 
                flexDirection: "column", 
                // height: "100%", 
                // overflowY: "auto", 
                backgroundColor: 
                theme.colors.background, 
                color: theme.colors.text, 
                padding: spacing.xxxs, 
                marginTop: "0px", 
                boxSizing: "border-box" }}
            >
            <div>
            <div
                style={{
                position: "sticky",
                top: 0, 
                zIndex: 999,
                backgroundColor: theme.colors.background
                }}
            >
                <Header page={ticker} />
                {/* Tab Navigation */}
                <div>
                    <nav ref={tabNavRef} style={styles.navBar}>
                        {TABS.map((tab) => (
                            <div key={tab} style={{ cursor: "pointer", padding: `${spacing.m}px ${spacing.xxs}px`, fontWeight: activeTab === tab ? "700" : "500", color: activeTab === tab ? theme.colors.contentPrimary : theme.colors.contentSecondary, borderBottom: activeTab === tab ? `2px solid ${theme.colors.contentPrimary}` : "none" }} onClick={() => handleTabClick(tab)}>
                                <Typography variant="button" type="utility">{tab}</Typography>
                            </div>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Tab Content Sections */}
            <div 
                class-name="tab-content" 
                ref={tabContentRef}
                style={{ 
                    // height: 812,
                    height: 2000,
                    overflowY: "auto",
                    padding: spacing.xxxs, 
                    scrollBehavior: "smooth", 
                    marginTop: topOffset, 
                    boxSizing: "border-box",
                }}>
                <div data-tab="Essentials" ref={sectionRefs["Essentials"]} style={{ padding: spacing.xxxs, borderBottom: `1px solid ${theme.colors.border}` }}>
                    
                    {tickerDetails && (
                        <CompanyName 
                            name={tickerDetails.name || "Company"} 
                            symbol={ticker} 
                            logo={tickerDetails?.branding?.icon_url || ""}
                        />
                    )}
                    
                    {tickerPrices && <TickerPrice prices={tickerPrices} hoverData={hoveredPriceData}/>}
                    
                    {/* Company Chart (Stock Price Over Time) */}
                    <div style={{ width: "100%", marginTop: "20px" }}>
                    <CompanyChart data={stockHistory} rawHistory={rawHistory} isPositive={tickerPrices.todaysChange >= 0} ticker={ticker} onPriceUpdate={handlePriceUpdate}/>
                    </div>

                    {/* Timeframe Selector */}
                    <TimeSlider timeframe={timeframe} setTimeframe={setTimeframe} />
                    
                    {/* Company Summary */}
                    <CompanySummary news={filteredNews} assets={assets} timeframe={timeframe} />
                    
                    {/* Filtered News */}
                    {filteredNews && <TickerNewsList tickerNews={filteredNews} isCompany={true} />}
                    
                    <img style={{ width: "100%" }} src={Spacer} alt="Spacer" />
                </div>

                <div data-tab="Key Stats" ref={sectionRefs["Key Stats"]} style={{ padding: spacing.xxxs, borderBottom: `1px solid ${theme.colors.border}` }}>
                    {
                        isPositive ? <img style={{ width: "100%" }} src={KeyStats} alt="Key Stats" /> : <img style={{ width: "100%" }} src={KeyStats_Neg} alt="Key Stats" />
                    }
                    <img style={{ width: "100%" }} src={Spacer} alt="Spacer" />
                </div>

                <div data-tab="Events" ref={sectionRefs["Events"]} style={{ padding: spacing.xxxs, borderBottom: `1px solid ${theme.colors.border}` }}>
                    <img style={{ width: "100%" }} src={Events} alt="Events" />
                    <img style={{ width: "100%" }} src={Spacer} alt="Spacer" />
                </div>

                <div data-tab="Vibes" ref={sectionRefs["Vibes"]} style={{ padding: spacing.xxxs, borderBottom: `1px solid ${theme.colors.border}` }}>
                    <img style={{ width: "100%" }} src={Vibes} alt="Vibes" />
                </div>

                <div data-tab="Company Profile" ref={sectionRefs["Company Profile"]} style={{ padding: spacing.xxxs, borderBottom: `1px solid ${theme.colors.border}` }}>
                    <img style={{ width: "100%" }} src={CompanyProfile} alt="CompanyProfile" />
                </div>
            </div>
            
            
        </div>
        </motion.div>
        </>
    );
}

export default Company;